import React, { useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";

// Ensure styles remain compatible
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { MaximizeIcon, MinimizeIcon } from "../constants/icons";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// --- Internal Helper: Modal Logic ---
const PdfModal = ({ isOpen, onClose, children }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-close-area" onClick={onClose} />
      <div className="modal-container">{children}</div>
    </div>
  );
};

// --- Internal Helper: Fullscreen Viewer Logic ---
const FullscreenViewer = ({ file, onClose }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [baseWidth, setBaseWidth] = useState(null);
  const [inputPage, setInputPage] = useState("1");
  const viewerRef = useRef(null);
  const isInternalScroll = useRef(false);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setInputPage("1");
  };

  useEffect(() => {
    let timeoutId = null;
    const updateWidth = () => {
      if (viewerRef.current) {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setBaseWidth(viewerRef.current.getBoundingClientRect().width * 0.9);
        }, 150);
      }
    };
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    if (viewerRef.current) ro.observe(viewerRef.current);
    return () => {
      ro.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (isInternalScroll.current) return;

      const container = viewerRef.current;
      if (!container) return;

      const pages = container.querySelectorAll(".page-wrap");
      let currentPage = 1;
      let minDiff = Infinity;

      pages.forEach((page) => {
        const rect = page.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const diff = Math.abs(rect.top - containerRect.top);

        if (diff < minDiff) {
          minDiff = diff;
          currentPage = parseInt(page.getAttribute("data-page-number"), 10);
        }
      });

      if (currentPage !== pageNumber) {
        setPageNumber(currentPage);
        setInputPage(String(currentPage));
      }
    };

    const container = viewerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (container) container.removeEventListener("scroll", handleScroll);
    };
  }, [pageNumber]);

  const goToPage = (p) => {
    const clamped = Math.max(1, Math.min(p, numPages || 1));
    setPageNumber(clamped);
    setInputPage(String(clamped));

    const container = viewerRef.current;
    if (container) {
      const targetPage = container.querySelector(
        `[data-page-number="${clamped}"]`,
      );
      if (targetPage) {
        isInternalScroll.current = true;
        targetPage.scrollIntoView({ behavior: "smooth", block: "start" });
        setTimeout(() => {
          isInternalScroll.current = false;
        }, 800);
      }
    }
  };

  const commitPageInput = () => {
    const p = parseInt(inputPage, 10);
    if (!isNaN(p)) goToPage(p);
    else setInputPage(String(pageNumber));
  };

  return (
    <div className="pdf-viewer-root">
      <div className="viewer-toolbar">
        <div className="page-input-wrap">
          <input
            className="page-input"
            type="number"
            min={1}
            max={numPages || 1}
            value={inputPage}
            onChange={(e) => setInputPage(e.target.value)}
            onBlur={commitPageInput}
            onKeyDown={(e) => e.key === "Enter" && commitPageInput()}
          />
          <span className="page-total">/ {numPages ?? "—"}</span>
        </div>

        <button
          className="icon-btn close-btn"
          onClick={onClose}
          data-tooltip="Exit Full Screen"
        >
          <MinimizeIcon />
        </button>
      </div>

      <div className="pdf-viewer-area" ref={viewerRef}>
        <div className="document-container">
          {baseWidth ? (
            <Document
              file={file}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <div className="loading-text">Preparing your document...</div>
              }
            >
              {numPages &&
                Array.from({ length: numPages }, (_, i) => (
                  <div key={i} className="page-wrap" data-page-number={i + 1}>
                    <Page
                      pageNumber={i + 1}
                      width={baseWidth}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      loading={
                        <div className="page-loading">
                          Loading page {i + 1}...
                        </div>
                      }
                    />
                  </div>
                ))}
            </Document>
          ) : (
            <div className="loading-text">Calculating layout...</div>
          )}
        </div>
      </div>

      <div className="floating-controls">
        <button
          className="nav-btn"
          onClick={() => goToPage(pageNumber - 1)}
          disabled={pageNumber <= 1}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <span className="page-indicator">
          {pageNumber} / {numPages || "—"}
        </span>
        <button
          className="nav-btn"
          onClick={() => goToPage(pageNumber + 1)}
          disabled={!numPages || pageNumber >= numPages}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
    </div>
  );
};

// --- Main Published Component ---
const PdfReader = ({
  file,
  showFullScreenButton = true,
  style,
  noOfPageInPreview = 1,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [containerWidth, setContainerWidth] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let timeoutId = null;
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setContainerWidth(entries[0].contentRect.width);
        }, 150);
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleOpenFullscreen = () => {
    setIsModalOpen(true);
  };

  const handleCloseFullscreen = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <div ref={containerRef} className="pdf-preview image-style" style={style}>
        {showFullScreenButton && (
          <div className="preview-header">
            <button
              className="fullscreen-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenFullscreen();
              }}
              data-tooltip="Full Screen"
            >
              <MaximizeIcon width="16" height="16" />
            </button>
          </div>
        )}

        <div className="preview-content">
          <Document
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={null}
          >
            {containerWidth && (
              <>
                {Array.from(
                  {
                    length:
                      noOfPageInPreview === Infinity
                        ? numPages || 0
                        : Math.min(noOfPageInPreview, numPages || 0),
                  },
                  (_, index) => (
                    <div className="page-wrapper" key={index}>
                      <Page
                        pageNumber={index + 1}
                        width={
                          containerWidth
                            ? Math.min(containerWidth, 1000)
                            : undefined
                        }
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </div>
                  ),
                )}
              </>
            )}
          </Document>
        </div>
      </div>

      <PdfModal isOpen={isModalOpen} onClose={handleCloseFullscreen}>
        <FullscreenViewer file={file} onClose={handleCloseFullscreen} />
      </PdfModal>
    </>
  );
};

export default PdfReader;
